import { create } from "zustand";
import { initialFold, projectSchedule } from "../lib/schedule-history/project-schedule";
import {
  canMergeAction, mergeAction, nextRedo, nextUndo,
  type AppHistoryAction, type HistoryScope,
} from "../lib/schedule-history/app-history-actions";
import { applyMetadataEvent } from "../lib/metadata-history/metadata-events";
import type { MetadataEvent } from "../lib/metadata-history/metadata-events";
import { metadataTargets } from "../lib/metadata-history/metadata-targets";
import type { ScheduleEvent } from "../lib/schedule-history/history-events";
import { useCustomArtStore } from "./custom-art-store";
import { useMetadataHistoryStore } from "./metadata-history-store";
import { useScheduleStore } from "./schedule-store";

type HistoryState = {
  events: AppHistoryAction[];
  undoneIds: string[];
  panelOpen: boolean;
  sealed: boolean;
  append: (event: ScheduleEvent) => void;
  appendMetadata: (sessionId: string, event: MetadataEvent) => void;
  undo: (scope?: HistoryScope) => void;
  redo: (scope?: HistoryScope) => void;
  jumpTo: (cursor: number) => void;
  sealMerge: () => void;
  togglePanel: () => void;
};

function applyProjections(events: AppHistoryAction[], undoneIds: string[]) {
  const applied = events.filter(action => !undoneIds.includes(action.event.id));
  const scheduleEvents = applied.flatMap(action => action.domain === "schedule" ? [action.event] : []);
  const projection = projectSchedule(initialFold, scheduleEvents, scheduleEvents.length);
  const metadataEvents: Record<string, MetadataEvent[]> = {};
  applied.forEach(action => {
    if (action.domain !== "metadata") return;
    const sessionEvents = metadataEvents[action.sessionId] ?? [];
    metadataEvents[action.sessionId] = [...sessionEvents, action.event];
  });
  // Restore all metadata sessions before a schedule preset switch activates one.
  useMetadataHistoryStore.getState().project(metadataEvents);
  useScheduleStore.setState(projection.schedule);
  const artwork = useCustomArtStore.getState();
  const selectedId = artwork.selectedId;
  const selectionExists = projection.layers.some(layer => layer.imageId === selectedId);
  const nextSelectedId = selectionExists ? selectedId : projection.layers.at(-1)?.imageId ?? null;
  const artworkChanged = artwork.layers !== projection.layers || selectedId !== nextSelectedId;
  if (artworkChanged) useCustomArtStore.setState({ layers: projection.layers, selectedId: nextSelectedId });
}

export const useHistoryStore = create<HistoryState>((set, get) => {
  const appendAction = (action: AppHistoryAction) => {
    const state = get();
    // Any new edit drops every undone branch, across domains and presets.
    const applied = state.events.filter(entry => !state.undoneIds.includes(entry.event.id));
    const last = applied.at(-1);
    const merges = canMergeAction(last, action, state.sealed);
    const events = merges && last
      ? [...applied.slice(0, -1), mergeAction(last, action)]
      : [...applied, action];
    set({ events, undoneIds: [], sealed: false });
    applyProjections(events, []);
  };

  return {
    events: [],
    undoneIds: [],
    panelOpen: false,
    sealed: true,

    append: event => appendAction({ domain: "schedule", event }),

    appendMetadata: (sessionId, event) => {
      const session = useMetadataHistoryStore.getState().sessions[sessionId];
      if (!session) return;
      const projection = applyMetadataEvent(session.projection, event);
      const layoutChanged = event.type === "day-layout/set"
        && JSON.stringify(session.dayLayouts?.[event.layout.mode]) !== JSON.stringify(event.layout);
      if (event.type !== "artwork/set" && !layoutChanged && JSON.stringify(projection) === JSON.stringify(session.projection)) return;
      const path = event.type === "position/set" ? event.path
        : event.type === "global/rotation" || event.type === "global/style" ? `globals.${event.index}` : undefined;
      const target = path ? metadataTargets(session.projection).find(target => target.path === path) : undefined;
      // Keep binding identity, so later layout replacements cannot relabel old moves.
      const identity = target ? { path: target.path, bind: target.bind, day: target.day } : undefined;
      appendAction({ domain: "metadata", sessionId, event, target: identity });
    },

    undo: scope => {
      const state = get();
      const action = nextUndo(state.events, state.undoneIds, scope);
      if (!action) return;
      const undoneIds = [...state.undoneIds, action.event.id];
      set({ undoneIds, sealed: true });
      applyProjections(state.events, undoneIds);
    },

    redo: scope => {
      const state = get();
      const action = nextRedo(state.events, state.undoneIds, scope);
      if (!action) return;
      const undoneIds = state.undoneIds.filter(id => id !== action.event.id);
      set({ undoneIds, sealed: true });
      applyProjections(state.events, undoneIds);
    },

    jumpTo: target => {
      const state = get();
      const cursor = Math.min(Math.max(target, 0), state.events.length);
      // Undo newest first, so the next redo restores the earliest missing action.
      const undoneIds = state.events.slice(cursor).map(action => action.event.id).reverse();
      set({ undoneIds, sealed: true });
      applyProjections(state.events, undoneIds);
    },

    sealMerge: () => set({ sealed: true }),
    togglePanel: () => set(state => ({ panelOpen: !state.panelOpen })),
  };
});
